/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */

import { html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import badgeStyles from './sh-badge.lit.scss.js';

/**
 * @element sh-badge
 * @prop {String} label - Defines the number or text that is shown inside the badge. For numbers a max of 4 digits is allowed.
 * @prop {String} type - Defines the color and icon shown inside the badge. Possible values are `error`, `warning` and `success`
 * @prop {Boolean} disabled - Defines if a tag is disabled. If set, disabled styles will be applied.
 */

export class SHBadge extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      type: { type: String, reflect: true },
      disabled: { type: Boolean, reflect: true },
      _processedLabel: { type: String, reflect: false, attribute: false },
    };
  }
  static get styles() {
    return [sharedStyles, badgeStyles];
  }

  render() {
    return (
      this._processedLabel || // if there is a label, render that and nothing else
      (this.type === 'error' || this.type === 'warning' || this.type === 'success'
        ? html`<sh-icon icon="${this.type}" size="xs"></sh-icon>`
        : '') // render icon otherwise in case it is legal
    );
  }

  updated(changedProperties) {
    if (changedProperties.has('label')) {
      this._processLabel();
    }
  }

  _processLabel() {
    this._processedLabel = this.label;
    if (this._processedLabel) {
      const isNumber = new RegExp('^[0-9]+$|^[0-9]+[+]$');
      // truncate numbers
      if (this.label.match(isNumber) !== null) {
        let badgeLabel = parseInt(this.label, 10);
        if (badgeLabel >= 1000 || this.label.toString().length > 3) {
          this.label = '999+';
        }
      }
    }
  }
}
customElements.define('sh-badge', SHBadge);
